import { useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';

// ─────────────────────────────────────────────────────────────────────────────
// LO PENDIENTE — una sola suscripción viva para toda la consola.
//
// fase63 — Antes, Hoy e Inversores pedían `useGetList('transactions')` sólo para
// contar y listar lo pendiente. El data provider de la casa trae la COLECCIÓN
// ENTERA en cada `getList` y filtra en el navegador (así fue siempre y así se
// mantiene: la Mesa lo necesita). Pero para «lo que espera decisión» eso es
// traer miles de documentos para mostrar un puñado.
//
// Acá hay UN `onSnapshot` con `where('status','==','pending')`, que baja sólo
// los pendientes y los mantiene al día sin releer nada. Es la misma consulta
// que ya hacía `usePendingCount` para el contador de la navegación — ahora
// devuelve también los documentos, así que el contador y las dos pantallas
// comparten una única suscripción en vez de abrir tres.
// ─────────────────────────────────────────────────────────────────────────────

export type TxPend = Record<string, any> & { id: string };

let docs: TxPend[] = [];
let suscrito = false;
let unsub: (() => void) | undefined;
const oyentes = new Set<(d: TxPend[]) => void>();

function toDate(value: any): any {
  if (value === null || value === undefined) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (Array.isArray(value)) return value.map(toDate);
  if (typeof value === 'object' && value.constructor === Object) {
    const out: Record<string, any> = {};
    for (const k of Object.keys(value)) out[k] = toDate(value[k]);
    return out;
  }
  return value;
}

function abrir() {
  if (suscrito) return;
  suscrito = true;
  try {
    unsub = firebase
      .firestore()
      .collection('transactions')
      .where('status', '==', 'pending')
      .onSnapshot(
        (snap) => {
          // Mismo tratamiento que el data provider: Timestamp → Date, y el id
          // como campo. Las pantallas leen igual que si viniera del provider.
          docs = snap.docs.map((d) => ({ id: d.id, ...toDate(d.data() ?? {}) }));
          oyentes.forEach((f) => f(docs));
        },
        () => {
          docs = [];
          oyentes.forEach((f) => f(docs));
        }
      );
  } catch {
    docs = [];
  }
}

export function usePendientes(): TxPend[] {
  const [v, setV] = useState<TxPend[]>(docs);

  useEffect(() => {
    abrir();
    const f = (d: TxPend[]) => setV(d);
    oyentes.add(f);
    setV(docs);
    return () => {
      oyentes.delete(f);
      // La última pantalla que se va cierra la suscripción.
      if (oyentes.size === 0) {
        unsub?.();
        unsub = undefined;
        suscrito = false;
      }
    };
  }, []);

  return v;
}

/** Las acciones que de verdad esperan una decisión de la mesa.
 *
 *  Un intercambio se resuelve contra el libro en el acto y un interés lo
 *  acredita el robot: ninguno de los dos espera a nadie, aunque pueda quedar un
 *  documento en `pending` mientras el robot lo liquida. */
const DECIDIBLES = new Set(['topup', 'withdraw']);

export const esDecidible = (t: any): boolean => DECIDIBLES.has(String(t?.action ?? ''));

/**
 * fase67 — Antes devolvía `usePendientes().length`, es decir TODO lo que está
 * en `pending`. La insignia de la navegación decía «Mesa 5» mientras la portada
 * decía «Para resolver 3», porque Hoy sí filtraba por acción decidible. Dos
 * números distintos para la misma pregunta, en la misma pantalla. La insignia
 * es una llamada a decidir: cuenta lo que se decide.
 */
export function usePendingCount(): number {
  const docs = usePendientes();
  return docs.filter(esDecidible).length;
}
